import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useOfferStore, useQuoteTotals } from '@/lib/store';
import { formatCurrency } from '@/lib/utils';
import { normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import { getQuoteFileStem } from '@shared/quote-naming';
import { QuoteHeader } from '@shared/schema';
import { normalizeVatRate } from '@/lib/quote-calculations';
import DocxExport from './docx-export';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle, 
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Database, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { exportQuoteDocx, DocxGenerationMethod } from '@/lib/document-export';
import { Download, FileText, FileDown, Calculator, Building2, Euro, Package, Users, Calendar, Save, Undo2, AlertCircle } from 'lucide-react';
import { saveAndGetQuoteData } from '@/lib/quote-data-service';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { useAuth } from '@/lib/auth';
import GAEBExport from './gaeb/GAEBExport';
import { SoftecExportButton } from './softec-export-button';

// Sortable Gewerk Item Component
function SortableGewerkItem({ id, name, items, netTotal }: { id: string, name: string, items: any[], netTotal: number }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="border-l-2 border-primary pl-2 py-2 bg-white rounded-sm shadow-sm mb-2"
    >
      <div className="flex justify-between items-center mb-1">
        <div className="flex-1 flex items-center">
          <button 
            className="mr-2 cursor-grab touch-none p-1 text-gray-400 hover:text-gray-600" 
            {...attributes} 
            {...listeners}
          >
            <GripVertical size={16} />
          </button>
          <span className="text-sm font-medium text-gray-800">{name}</span>
        </div>
        <span className="text-sm font-medium">{formatCurrency(netTotal)}</span>
      </div>
      <div className="text-xs text-gray-600 ml-7">
        {items.length} Position{items.length !== 1 ? 'en' : ''}
      </div>
    </div>
  );
}

export default function QuoteSidebar() {
  // Individual selectors — whole-store useOfferStore() causes React #185
  const positions = useOfferStore(s => s.positions);
  const gewerkOrder = useOfferStore(s => s.gewerkOrder);
  const reorderGewerk = useOfferStore(s => s.reorderGewerk);
  const quoteItems = useOfferStore(s => s.quoteItems);
  const quoteHeader = useOfferStore(s => s.quoteHeader);
  const discountAmount = useOfferStore(s => s.discountAmount);
  const discountType = useOfferStore(s => s.discountType);
  const discountApplicationMode = useOfferStore(s => s.discountApplicationMode);
  const setDiscount = useOfferStore(s => s.setDiscount);
  const setDiscountApplicationMode = useOfferStore(s => s.setDiscountApplicationMode);
  const applyDistributedDiscount = useOfferStore(s => s.applyDistributedDiscount);
  const clearDiscount = useOfferStore(s => s.clearDiscount);
  const isDiscountAppliedToPrices = useOfferStore(s => s.isDiscountAppliedToPrices);
  const originalPrices = useOfferStore(s => s.originalPrices);
  const currentQuoteSlot = useOfferStore(s => s.currentQuoteSlot);
  const setCurrentQuoteSlot = useOfferStore(s => s.setCurrentQuoteSlot);
  const setGlobalVatRate = useOfferStore(s => s.setGlobalVatRate);
  const isGAEBDatabase = useOfferStore(s => s.isGAEBDatabase);
  const currentDatabaseId = useOfferStore(s => s.currentDatabaseId);

  const { gewerkGroups, totalNet } = useQuoteTotals();
  const { hasInvoiceAccess } = useAuth();
  const [, navigate] = useLocation();
  const [isGewerkOpen, setIsGewerkOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const { toast } = useToast();
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveStatusResetRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveInFlightRef = useRef(false);
  const pendingAutoSaveRef = useRef(false);
  const lastPersistedSnapshotRef = useRef<string | null>(null);
  const latestSnapshotRef = useRef<string>('');
  const autoSaveOfferNumberRef = useRef(
    `A-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`
  );
  const fallbackDateRef = useRef(new Date().toISOString().split('T')[0]);

  const vatRate = normalizeVatRate(quoteHeader.mwstSatz);
  const hasTotalDiscount = !isDiscountAppliedToPrices && discountAmount > 0;
  const totalDiscountValueRaw = hasTotalDiscount
    ? (discountType === 'percent'
      ? (totalNet * discountAmount) / 100
      : discountAmount)
    : 0;
  const totalDiscountValue = Math.max(0, Math.min(totalNet, Math.round(totalDiscountValueRaw * 100) / 100));
  const netAfterTotalDiscount = Math.max(0, Math.round((totalNet - totalDiscountValue) * 100) / 100);
  const totalTaxAfterDiscount = Math.round(netAfterTotalDiscount * (vatRate / 100) * 100) / 100;
  const totalGrossAfterDiscount = Math.round((netAfterTotalDiscount + totalTaxAfterDiscount) * 100) / 100;
  const normalizedEditorState = normalizePersistedQuoteEditorState({
    discountAmount,
    discountType,
    discountApplicationMode,
    originalPrices,
    isDiscountAppliedToPrices,
  });
  const editorSnapshot = JSON.stringify({
    quoteHeader,
    quoteItems,
    positions,
    gewerkOrder,
    editorState: normalizedEditorState,
  });
  
  // Local state for discount input to prevent recalculation on every keystroke
  const [localDiscountAmount, setLocalDiscountAmount] = useState<string>(discountAmount.toString());
  const discountDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // discountAmount sync removed — causes React #185 nested update on mount.
  // useState initializer already has correct value. clearDiscount re-renders with new value.
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (discountDebounceRef.current) {
        clearTimeout(discountDebounceRef.current);
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      if (autoSaveStatusResetRef.current) {
        clearTimeout(autoSaveStatusResetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    latestSnapshotRef.current = editorSnapshot;
  }, [editorSnapshot]);

  const scheduleAutoSaveStatusReset = useCallback((delayMs: number) => {
    if (autoSaveStatusResetRef.current) {
      clearTimeout(autoSaveStatusResetRef.current);
    }

    autoSaveStatusResetRef.current = setTimeout(() => {
      setAutoSaveStatus('idle');
    }, delayMs);
  }, []);
  
  // Debounced discount update - only update store after user stops typing
  const handleDiscountChange = useCallback((value: string) => {
    const normalizedInput = value.replace(',', '.');
    if (normalizedInput !== '' && !/^\d*\.?\d{0,2}$/.test(normalizedInput)) {
      return;
    }

    setLocalDiscountAmount(normalizedInput);
    
    // Clear any pending debounce
    if (discountDebounceRef.current) {
      clearTimeout(discountDebounceRef.current);
    }
    
    // Debounce the store update to avoid recalculating on every keystroke
    discountDebounceRef.current = setTimeout(() => {
      const parsedValue = parseFloat(normalizedInput) || 0;
      const clampedValue = discountType === 'percent'
        ? Math.min(100, Math.max(0, parsedValue))
        : Math.max(0, parsedValue);
      // Only update store, don't apply to prices yet (that happens when clicking the button)
      setDiscount(clampedValue, discountType, false); // false = don't apply to prices
    }, 300);
  }, [discountType, setDiscount]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for Gewerk reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = gewerkOrder.findIndex(id => id === active.id);
      const newIndex = gewerkOrder.findIndex(id => id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderGewerk(oldIndex, newIndex);
      }
    }
  };

  const persistQuoteProgress = useCallback(async (mode: 'manual' | 'auto' = 'manual') => {
    const isAutoSave = mode === 'auto';

    if (isGAEBDatabase) {
      return false;
    }

    if (quoteItems.length === 0) {
      if (!isAutoSave) {
        toast({
          title: "Keine Daten zum Speichern",
          description: "Fügen Sie erst Positionen mit Mengen hinzu, bevor Sie speichern.",
          variant: "destructive"
        });
      }
      return false;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    if (isAutoSave) {
      if (autoSaveInFlightRef.current) {
        pendingAutoSaveRef.current = true;
        return false;
      }
      autoSaveInFlightRef.current = true;
      setAutoSaveStatus('saving');
    } else {
      setIsSaving(true);
      pendingAutoSaveRef.current = false;
    }

    const snapshotAtSave = latestSnapshotRef.current;

    try {
      const editorStateForSave = normalizePersistedQuoteEditorState({
        discountAmount,
        discountType,
        discountApplicationMode,
        originalPrices,
        isDiscountAppliedToPrices,
      });
      const offerNumber = quoteHeader.angebot_nr?.trim() || autoSaveOfferNumberRef.current;
      const customerName = quoteHeader.kunde?.trim() || 'Arbeitsfortschritt';
      const safeDate = quoteHeader.datum || fallbackDateRef.current;
      const filename = `${getQuoteFileStem({
        header: {
          ...quoteHeader,
          angebot_nr: offerNumber,
          kunde: customerName,
        },
      }, 'Arbeitsfortschritt')} (Arbeitsfortschritt)`;

      const savedData = await saveAndGetQuoteData(
        {
          ...quoteHeader,
          angebot_nr: offerNumber,
          kunde: customerName,
          datum: safeDate,
        },
        quoteItems,
        positions,
        gewerkOrder,
        filename,
        currentQuoteSlot ?? undefined,
        editorStateForSave
      );

      const savedSlot = (savedData.metadata as any)?.slot as number | undefined;
      if (savedSlot && !currentQuoteSlot) {
        setCurrentQuoteSlot(savedSlot);
      }

      lastPersistedSnapshotRef.current = snapshotAtSave;
      setLastAutoSave(new Date());
      setAutoSaveStatus('saved');
      scheduleAutoSaveStatusReset(2000);

      if (!isAutoSave) {
        toast({
          title: "Arbeitsfortschritt gespeichert",
          description: `"${offerNumber}" wurde erfolgreich gespeichert. Sie können später weiterarbeiten oder exportieren.`,
          variant: "default"
        });
      }

      return true;
    } catch (error) {
      console.error(`Error ${isAutoSave ? 'auto-saving' : 'saving'} work progress:`, error);

      if (isAutoSave) {
        setAutoSaveStatus('error');
        scheduleAutoSaveStatusReset(3000);
      } else {
        toast({
          title: "Fehler beim Speichern",
          description: error instanceof Error ? error.message : 'Unbekannter Fehler beim Speichern des Arbeitsfortschritts',
          variant: "destructive"
        });
      }

      return false;
    } finally {
      if (isAutoSave) {
        autoSaveInFlightRef.current = false;

        if (
          pendingAutoSaveRef.current &&
          latestSnapshotRef.current !== lastPersistedSnapshotRef.current &&
          quoteItems.length > 0
        ) {
          pendingAutoSaveRef.current = false;
          autoSaveTimerRef.current = setTimeout(() => {
            void persistQuoteProgress('auto');
          }, 3000);
        }
      } else {
        setIsSaving(false);
      }
    }
  }, [
    currentQuoteSlot,
    discountAmount,
    discountApplicationMode,
    discountType,
    gewerkOrder,
    isDiscountAppliedToPrices,
    isGAEBDatabase,
    originalPrices,
    positions,
    quoteHeader,
    quoteItems,
    scheduleAutoSaveStatusReset,
    setCurrentQuoteSlot,
    toast,
  ]);

  useEffect(() => {
    if (isGAEBDatabase) {
      return;
    }

    if (lastPersistedSnapshotRef.current === null) {
      lastPersistedSnapshotRef.current = editorSnapshot;
      return;
    }

    if (editorSnapshot === lastPersistedSnapshotRef.current) {
      pendingAutoSaveRef.current = false;
      return;
    }

    if (quoteItems.length === 0) {
      return;
    }

    if (autoSaveInFlightRef.current) {
      pendingAutoSaveRef.current = true;
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      void persistQuoteProgress('auto');
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [editorSnapshot, isGAEBDatabase, persistQuoteProgress, quoteItems.length]);

  // Handle save work progress
  const handleSaveProgress = async () => {
    // For GAEB databases, save directly to GAEB endpoint
    if (isGAEBDatabase && currentDatabaseId) {
      setIsSaving(true);
      try {
        // Get all modified positions (items with ourPrice, ourComment, or adjustedQuantity)
        const entriesToSave = positions
          .filter(p => (p as any).type === 'item')
          .map(p => {
            const gaebEntry = p as any;
            // CRITICAL: Use null instead of undefined for reset values
            // JSON.stringify drops undefined values, but preserves null
            // This allows the server to distinguish between "not sent" and "reset to original"
            const adjustedQty = gaebEntry.adjustedQuantity;
            const price = gaebEntry.ourPrice;
            const comment = gaebEntry.ourComment;
            return {
              // CRITICAL: artikelId must match the database entry's artikelId for updates to work
              artikelId: gaebEntry.artikelId || p.originalId,
              ourPrice: price === undefined ? null : price,
              // null signals "clear comment" - same pattern as price/quantity
              ourComment: (comment === undefined || comment === '') ? null : comment,
              adjustedQuantity: adjustedQty === undefined ? null : adjustedQty
            };
          });

        if (entriesToSave.length === 0) {
          toast({
            title: "Keine Daten zum Speichern",
            description: "Es wurden keine LV-Positionen gefunden.",
            variant: "destructive"
          });
          return;
        }

        const response = await fetch('/api/gaeb/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            databaseId: currentDatabaseId,
            entries: entriesToSave
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        toast({
          title: "X83-Daten gespeichert",
          description: `${entriesToSave.length} Positionen wurden gespeichert.`,
          variant: "default"
        });

        console.log('GAEB data saved successfully:', entriesToSave.length, 'entries');
      } catch (error) {
        console.error('Error saving GAEB data:', error);
        toast({
          title: "Fehler beim Speichern",
          description: error instanceof Error ? error.message : 'Unbekannter Fehler beim Speichern',
          variant: "destructive"
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    await persistQuoteProgress('manual');
  };

  // Export quote (creates invoice draft)
  const handleCreateInvoice = async () => {
    const normalizedItems = quoteItems
      .map((q) => {
        const quantity = Number(q.menge) || 0;
        const unitPrice = Number(q.preis) || 0;
        const vatRate = Number(q.umsatzsteuer) || 19;
        return {
          ...q,
          quantity,
          unitPrice,
          vatRate,
        };
      })
      .filter((q) => q.quantity > 0);

    if (normalizedItems.length === 0) {
      toast({
        title: 'Keine Positionen mit Menge vorhanden',
        description: 'Bitte geben Sie zuerst Mengen ein.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingInvoice(true);
    try {
      let effectiveSlot = currentQuoteSlot ?? undefined;
      if (!effectiveSlot) {
        const editorStateForSave = normalizePersistedQuoteEditorState({
          discountAmount,
          discountType,
          discountApplicationMode,
          originalPrices,
          isDiscountAppliedToPrices,
        });
        const savedData = await saveAndGetQuoteData(
          quoteHeader,
          quoteItems,
          positions,
          gewerkOrder,
          undefined,
          currentQuoteSlot ?? undefined,
          editorStateForSave
        );
        const savedSlot = (savedData?.metadata as any)?.slot as number | undefined;
        if (savedSlot && savedSlot > 0) {
          effectiveSlot = savedSlot;
          setCurrentQuoteSlot(savedSlot);
        }
      }

      const searchParams = new URLSearchParams(window.location.search);
      const softecProjectId = searchParams.get('projectId')?.trim() || undefined;

      const res = await fetch('/api/quotes/to-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positions: normalizedItems.map((q, index) => ({
            title: (q.name || q.beschreibung || `Position ${index + 1}`).toString().trim(),
            description: q.beschreibung,
            quantity: q.quantity,
            unit: (q.einheit || 'Stk').toString().trim() || 'Stk',
            unitPrice: q.unitPrice,
            vatRate: q.vatRate,
            positionNumber: q.positionNumber,
            gewerk: q.gewerk,
            budgetTaskId: q.budgetTaskId,
          })),
          clientName: quoteHeader.kunde,
          clientAddress: quoteHeader.anschrift,
          projectName: quoteHeader.bauvorhaben,
          softecProjectId,
          slot: effectiveSlot,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Konvertierung fehlgeschlagen');
      }

      if (data?.data?.invoice?.id) {
        toast({
          title: 'Rechnungsentwurf erstellt',
          description: `Entwurf mit ${normalizedItems.length} Positionen erstellt.`,
        });
        navigate(`/invoices?edit=${data.data.invoice.id}`);
      } else {
        throw new Error('Kein Export möglich');
      }
    } catch (error) {
      console.error('Error exporting quote:', error);
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Rechnung konnte nicht erstellt werden',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4 space-y-6">
          


          {/* Gewerk Order Section with Drag & Drop */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <Collapsible open={isGewerkOpen} onOpenChange={setIsGewerkOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                    <h3 className="text-lg font-semibold text-gray-900">Gewerke</h3>
                    {isGewerkOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2">
                  <p className="text-sm text-gray-600 mb-3">
                    Ziehen Sie die Gewerke, um die Reihenfolge für den Export zu ändern:
                  </p>
                  
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={gewerkOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      {gewerkGroups.map((group) => (
                        <SortableGewerkItem
                          key={group.name}
                          id={group.name}
                          name={group.name}
                          items={group.items}
                          netTotal={group.netTotal}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Discount Section */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Rabatt</h3>
              <div className={`p-4 rounded-lg space-y-3 ${isDiscountAppliedToPrices ? 'bg-orange-50 border-2 border-orange-300' : 'bg-purple-50'}`}>
                
                {/* Active Discount Warning Banner */}
                {isDiscountAppliedToPrices && (
                  <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-orange-800">
                        Preise wurden um {discountAmount}% reduziert
                      </p>
                      <p className="text-xs text-orange-600">
                        Alle Einzelpreise sind rabattiert
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearDiscount}
                      className="h-7 px-2 text-xs bg-white border-orange-400 text-orange-700 hover:bg-orange-50"
                    >
                      <Undo2 className="h-3 w-3 mr-1" />
                      Rückgängig
                    </Button>
                  </div>
                )}

                {/* Discount Type Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={discountType === 'percent' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDiscount(parseFloat(localDiscountAmount) || 0, 'percent', false)}
                    className="flex-1"
                    disabled={isDiscountAppliedToPrices}
                  >
                    %
                  </Button>
                  <Button
                    variant={discountType === 'absolute' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDiscount(parseFloat(localDiscountAmount) || 0, 'absolute', false)}
                    className="flex-1"
                    disabled={isDiscountAppliedToPrices}
                    title="Fester Euro-Betrag als Rabatt"
                  >
                    €
                  </Button>
                </div>

                {/* Discount Input */}
                <div className="space-y-1">
                  <Label htmlFor="discount-amount" className="text-xs text-gray-600">
                    Rabatt ({discountType === 'percent' ? '%' : '€'})
                  </Label>
                  <Input
                    id="discount-amount"
                    type="text"
                    inputMode="decimal"
                    value={localDiscountAmount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    placeholder="0"
                    className="text-sm"
                    disabled={isDiscountAppliedToPrices}
                  />
                </div>

                {/* Apply to Einzelpreise Button */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Einzelpreise rabattieren:</Label>
                  <Button
                    variant={isDiscountAppliedToPrices ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => applyDistributedDiscount()}
                    className={`w-full text-xs ${isDiscountAppliedToPrices ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 hover:bg-orange-50'}`}
                    disabled={isDiscountAppliedToPrices || discountType === 'absolute' || parseFloat(localDiscountAmount) <= 0}
                    title={discountType === 'absolute' ? 'Nur mit Prozent-Rabatt möglich' : parseFloat(localDiscountAmount) <= 0 ? 'Bitte erst Rabatt eingeben' : 'Rabatt wird auf alle Einzelpreise angewendet'}
                  >
                    {isDiscountAppliedToPrices 
                      ? `✓ ${discountAmount}% angewandt` 
                      : `Alle Einzelpreise um ${localDiscountAmount || '0'}% reduzieren`}
                  </Button>
                  {parseFloat(localDiscountAmount) > 0 && !isDiscountAppliedToPrices && discountType === 'percent' && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Dies ändert alle {quoteItems.length} Einzelpreise dauerhaft
                    </p>
                  )}
                </div>

                {/* Discount Preview */}
                {parseFloat(localDiscountAmount) > 0 && !isDiscountAppliedToPrices && (
                  <div className="bg-white p-2 rounded border border-purple-200 space-y-1">
                    <div className="text-xs text-gray-600">
                      {discountType === 'percent' 
                        ? `${localDiscountAmount}% von Gesamtsumme (${quoteItems.length} Positionen)`
                        : `${formatCurrency(parseFloat(localDiscountAmount) || 0)} von Gesamtsumme`
                      }
                    </div>
                    <div className="text-sm font-medium text-purple-600">
                      Ersparnis: {formatCurrency(
                        discountType === 'percent'
                          ? (totalNet * (parseFloat(localDiscountAmount) || 0)) / 100
                          : parseFloat(localDiscountAmount) || 0
                      )}
                    </div>
                  </div>
                )}

                {/* Show savings summary when prices are modified */}
                {isDiscountAppliedToPrices && Object.keys(originalPrices).length > 0 && (
                  <div className="bg-white p-2 rounded border border-orange-200 space-y-1">
                    <div className="text-xs text-gray-600">Gesamtersparnis:</div>
                    <div className="text-sm font-medium text-green-600">
                      {formatCurrency(
                        Object.entries(originalPrices).reduce((sum, [id, origPrice]) => {
                          const item = quoteItems.find(q => q.id === parseInt(id));
                          if (item) {
                            const currentPrice = typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis;
                            return sum + ((origPrice - currentPrice) * (item.menge || 0));
                          }
                          return sum;
                        }, 0)
                      )}
                    </div>
                  </div>
                )}

                {/* Clear Button - show when there's a discount value */}
                {(parseFloat(localDiscountAmount) > 0 || isDiscountAppliedToPrices) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearDiscount();
                      setLocalDiscountAmount('0');
                    }}
                    className="w-full text-xs"
                  >
                    {isDiscountAppliedToPrices ? 'Rabatt zurücksetzen' : 'Rabatt löschen'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Grand Total */}
          {gewerkGroups.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Gesamtsumme</h3>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between gap-3 pb-1">
                  <Label className="text-xs text-gray-600">MwSt-Satz</Label>
                  <Select
                    value={String(vatRate)}
                    onValueChange={(value) => setGlobalVatRate(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-24 text-xs border-blue-200 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="19">19%</SelectItem>
                      <SelectItem value="7">7%</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Netto:</span>
                  <span>{formatCurrency(totalNet)}</span>
                </div>
                
                {/* Discount Line - only show when NOT applied to individual prices */}
                {hasTotalDiscount && (
                  <div className="flex justify-between text-sm text-purple-600 font-medium">
                    <span>Rabatt ({discountType === 'percent' ? `${discountAmount}%` : `${formatCurrency(discountAmount)}`}):</span>
                    <span>-{formatCurrency(totalDiscountValue)}</span>
                  </div>
                )}

                {hasTotalDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Netto nach Rabatt:</span>
                    <span>{formatCurrency(netAfterTotalDiscount)}</span>
                  </div>
                )}
                
                {/* Applied Discount Info */}
                {isDiscountAppliedToPrices && (
                  <div className="flex justify-between text-sm text-orange-600 font-medium">
                    <span>Rabatt ({discountAmount}% angewandt):</span>
                    <span className="text-green-600">✓ in Preisen</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">MwSt. ({vatRate}%):</span>
                  <span>{formatCurrency(totalTaxAfterDiscount)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t border-blue-200 pt-2">
                  <span>Brutto:</span>
                  <span className="text-blue-900">
                    {formatCurrency(totalGrossAfterDiscount)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Statistics */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Statistiken</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{quoteItems.length}</div>
                <div className="text-xs text-blue-600">Ausgewählt</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{gewerkGroups.length}</div>
                <div className="text-xs text-green-600">Gewerke</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Export Section at Bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
        <div className="p-3 space-y-2">
          {/* Auto-Save Status (compact inline) */}
          {quoteItems.length > 0 && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-xs min-w-0">
                {autoSaveStatus === 'saving' && (
                  <>
                    <div className="w-2 h-2 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <span className="text-blue-600 truncate">Speichert...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                    <span className="text-green-600 truncate">Gespeichert {lastAutoSave && lastAutoSave.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-red-600 truncate">Speichern fehlgeschlagen</span>
                )}
                {autoSaveStatus === 'idle' && lastAutoSave && (
                  <span className="text-gray-400 truncate">{lastAutoSave.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
              <Button
                onClick={handleSaveProgress}
                disabled={isSaving}
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                {isSaving ? (
                  <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          )}

          {/* PDF Export (primary action) */}
          <DocxExport />

          {/* Secondary exports */}
          <div className="space-y-1.5">
            <SoftecExportButton
              slot={currentQuoteSlot || 0}
              disabled={quoteItems.length === 0}
            />
            <GAEBExport />
            {hasInvoiceAccess && (
              <Button
                onClick={handleCreateInvoice}
                disabled={isCreatingInvoice || quoteItems.filter(q => q.menge > 0).length === 0}
                variant="outline"
                size="sm"
                className="w-full flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-50"
              >
                {isCreatingInvoice ? (
                  <>
                    <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    Erstelle...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4" />
                    Rechnung erstellen
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
